import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Stack,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import InfoIcon from '@mui/icons-material/Info';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import { isInApp } from '../utils/environment';

// 自定义样式组件
const StatusBar = styled(Box)(({ theme }) => ({
  height: 44,
  backgroundColor: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 16px',
  position: 'sticky',
  top: 0,
  zIndex: 1100,
  borderBottom: '1px solid #F0F0F0',
}));

const BestValueBadge = styled(Box)(({ theme }) => ({
  backgroundColor: '#007AFF',
  color: 'white',
  padding: '4px 12px',
  borderRadius: 8,
  fontSize: 14,
  fontWeight: 500,
}));

const PaymentMethodCard = styled(Card)(({ theme, selected }) => ({
  border: selected ? `2px solid #007AFF` : '1px solid #E5E7EB',
  borderRadius: 8,
  cursor: 'pointer',
  '&:hover': {
    borderColor: '#007AFF',
  },
}));

const Pay = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { couponTemplateListItem } = location.state || {};
  const [selectedPayment, setSelectedPayment] = useState('payCardBO'); // 默认选中信用卡

  // 处理支付方式选择
  const handlePaymentSelect = (method) => {
    setSelectedPayment(method);
  };

  // 处理支付按钮点击
  const handleProceedToPayment = () => {
    navigate('/campaigns/invitations/pay-card', { 
      state: { 
        couponTemplateListItem,
        methodType: selectedPayment // 传递选中的支付方式
      } 
    });
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F9FAFB' }}>
      {/* 状态栏 */}
      {
        !isInApp() && (
          <StatusBar>
          <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate(-1)}
              sx={{ color: 'black' }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
            <Typography 
              variant="h6" 
              sx={{ 
                flexGrow: 1, 
                textAlign: 'center', 
                fontWeight: 600,
                fontSize: 17,
              }}
            >
              Reward Details
            </Typography>
            <Box sx={{ width: 40 }} /> {/* 平衡布局 */}
          </Box>
        </StatusBar>
        )
      }

      <Container maxWidth="sm" sx={{ pt: 2, pb: 10 }}>
        {/* 优惠券价值展示 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <BestValueBadge sx={{ display: 'inline-block', mb: 2 }}>
              Best Value
            </BestValueBadge>
            <Typography variant="h3" component="div" sx={{ fontWeight: 700, mb: 1 }}>
              ${couponTemplateListItem?.facePrice}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Pay only ${couponTemplateListItem?.actualPrice}
            </Typography>
            {/* <Typography sx={{ fontWeight: 500, color: '#007AFF' }}>
              Save 50%
            </Typography> */}
          </CardContent>
        </Card>

        {/* 优惠券详情 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Coupon Details
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Coupon Code</Typography>
                <Typography fontWeight={500}>{couponTemplateListItem?.name}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Valid Until</Typography>
                <Typography fontWeight={500}>{couponTemplateListItem?.num} {couponTemplateListItem?.unit}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Minimum Purchase</Typography>
                <Typography fontWeight={500}>${couponTemplateListItem?.facePrice}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Usage Limit</Typography>
                <Typography fontWeight={500}>Non-refundable</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 条款和条件 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Terms & Conditions
            </Typography>
            <List disablePadding>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <InfoIcon sx={{ color: '#8E8E93' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Eligibility"
                  secondary="Valid for all users. One coupon per person."
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <AccessTimeIcon sx={{ color: '#8E8E93' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Validity Period"
                  secondary="Coupon must be used within 90 days of purchase."
                />
              </ListItem>
              <ListItem sx={{ px: 0 }}>
                <ListItemIcon>
                  <ErrorOutlineIcon sx={{ color: '#8E8E93' }} />
                </ListItemIcon>
                <ListItemText 
                  primary="Non-Refundable"
                  secondary="Once purchased, coupons cannot be refunded or exchanged."
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>

        {/* 支付方式 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Payment Method
            </Typography>
            <Stack spacing={2}>
              <PaymentMethodCard 
                selected={selectedPayment === 'payCardBO'}
                onClick={() => handlePaymentSelect('payCardBO')}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '16px',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CreditCardIcon sx={{ mr: 2, color: '#8E8E93' }} />
                    <Box>
                      <Typography fontWeight={500}>Credit Card</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Visa, Mastercard, American Express
                      </Typography>
                    </Box>
                  </Box>
                  {selectedPayment === 'payCardBO' && (
                    <CheckCircleIcon sx={{ color: '#007AFF' }} />
                  )}
                </CardContent>
              </PaymentMethodCard>

              <PaymentMethodCard 
                selected={selectedPayment === 'payEcheckBO'}
                onClick={() => handlePaymentSelect('payEcheckBO')}
              >
                <CardContent sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  padding: '16px',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AccountBalanceIcon sx={{ mr: 2, color: '#8E8E93' }} />
                    <Box>
                      <Typography fontWeight={500}>Bank Transfer</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Direct bank transfer
                      </Typography>
                    </Box>
                  </Box>
                  {selectedPayment === 'payEcheckBO' && (
                    <CheckCircleIcon sx={{ color: '#007AFF' }} />
                  )}
                </CardContent>
              </PaymentMethodCard>
            </Stack>
          </CardContent>
        </Card>

        {/* 订单摘要 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Order Summary
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Coupon Value</Typography>
                <Typography fontWeight={500}>${couponTemplateListItem?.facePrice}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Processing Fee</Typography>
                <Typography fontWeight={500}>$0.00</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontWeight={600}>Total Amount</Typography>
                <Typography fontWeight={700}>${couponTemplateListItem?.actualPrice}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 底部按钮 */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleProceedToPayment}
          sx={{
            bgcolor: 'black',
            color: 'white',
            py: 2,
            borderRadius: 12,
            textTransform: 'none',
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100% - 32px)',
            fontWeight: 600,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              bgcolor: '#333',
            },
          }}
        >
          Proceed to Payment
        </Button>
      </Container>
    </Box>
  );
};

export default Pay; 